import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import useSEO from '@/hooks/useSEO';

const values = [
  { icon: 'ri-medal-line', title: 'Premium Quality', desc: 'We never compromise on materials. Every piece of glass we install is tempered safety glass, and every hardware component is commercial grade.' },
  { icon: 'ri-heart-line', title: 'Family Owned', desc: 'Quinolux is a family-owned business built on trust, integrity, and a genuine passion for beautiful glass work.' },
  { icon: 'ri-map-pin-line', title: 'Las Vegas Proud', desc: 'We\'ve been serving the Las Vegas Valley for 15+ years. This is our home, and we take pride in making it more beautiful.' },
];

const team = [
  { name: 'Carlos Mendez', role: 'Founder & Master Installer', years: '15+ years', img: 'https://public.readdy.ai/ai/img_res/3940bf31e792f0ddcd0936e19324e135.jpg' },
  { name: 'Maria Mendez', role: 'Operations Manager', years: '12+ years', img: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20Hispanic%20woman%20in%20her%2030s%2C%20warm%20smile%2C%20business%20casual%20attire%2C%20operations%20manager%2C%20professional%20headshot%20clean%20background&width=400&height=400&seq=team002&orientation=squarish' },
  { name: 'James Rivera', role: 'Lead Glass Technician', years: '10+ years', img: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20man%20smiling%2C%20wearing%20work%20uniform%2C%20glass%20technician%2C%20professional%20headshot%20clean%20white%20background&width=400&height=400&seq=team003&orientation=squarish' },
  { name: 'Sofia Torres', role: 'Design Consultant', years: '8+ years', img: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20woman%20smiling%2C%20business%20casual%2C%20interior%20design%20consultant%2C%20professional%20headshot%20clean%20background&width=400&height=400&seq=team004&orientation=squarish' },
];

const AboutPage = () => {
  useSEO({
    title: 'About Quinolux Glass & Mirrors | Las Vegas Glass Company',
    description: 'Learn about Quinolux Glass & Mirrors — Las Vegas\'s premier custom glass company. Family-owned, 15+ years experience, 500+ five-star reviews. Frameless shower doors, glass railings, custom mirrors.',
    keywords: 'about Quinolux glass Las Vegas, glass company Las Vegas history, custom glass installer Las Vegas',
    canonical: 'https://quinoluxglass.com/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'About Quinolux Glass & Mirrors',
        description: 'Family-owned Las Vegas glass company with 15+ years experience. Specializing in frameless shower doors, glass railings, and custom mirrors.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/about`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'About', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/about` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Quinolux Glass & Mirrors',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}`,
        foundingDate: '2009',
        founder: { '@type': 'Person', name: 'Carlos Mendez' },
        numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
        description: 'Family-owned custom glass company in Las Vegas, NV. Serving the Las Vegas Valley since 2009.',
        telephone: '+17026042093',
        email: 'info@quinoluxglass.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          addressCountry: 'US',
        },
      },
    ],
  });

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 opacity-25">
          <img src="https://readdy.ai/api/search-image?query=glass%20installation%20team%20working%20in%20luxury%20Las%20Vegas%20home%2C%20professional%20craftsmen%20installing%20frameless%20shower%20door%2C%20teamwork%2C%20professional%20photography&width=1920&height=700&seq=abouthero&orientation=landscape" alt="" className="w-full h-full object-cover object-top" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">Our Story</span>
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            About Quinolux
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Las Vegas's premier custom glass company — family-owned, locally operated, and dedicated to transforming spaces with precision glass work since 2009.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-light text-black mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Built on Glass,<br /><span className="font-semibold">Built on Trust</span>
              </h2>
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                <p>Quinolux Glass & Mirrors was founded in 2009 by Carlos Mendez, a master glass installer with a vision: to bring luxury-level glass craftsmanship to Las Vegas homeowners and businesses at fair, transparent prices.</p>
                <p>What started as a one-man operation out of a small workshop has grown into one of the most trusted glass companies in the Las Vegas Valley, with a team of certified installers and a reputation built on 500+ five-star reviews.</p>
                <p>We specialize in frameless shower doors, glass railings, custom mirrors, tub enclosures, and glass partitions — serving Las Vegas, Henderson, Summerlin, North Las Vegas, Spring Valley, and Paradise.</p>
                <p>Every project we take on is treated like it's our own home. We measure twice, cut once, and never leave until the installation is perfect.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-black text-white text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                  Our Services <i className="ri-arrow-right-line"></i>
                </Link>
                <a href="tel:+17026042093" className="inline-flex items-center justify-center gap-2 border border-gray-200 text-black text-sm font-medium px-7 py-3.5 rounded-full hover:border-black transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line"></i> (702) 604-2093
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden h-64">
                <img src="https://readdy.ai/api/search-image?query=luxury%20frameless%20glass%20shower%20door%20installation%20completed%20in%20modern%20bathroom%2C%20beautiful%20result%2C%20professional%20photography%2C%20Las%20Vegas%20home&width=500&height=400&seq=about001&orientation=landscape" alt="Quinolux glass installation" className="w-full h-full object-cover object-top" />
              </div>
              <div className="rounded-2xl overflow-hidden h-64 mt-8">
                <img src="https://readdy.ai/api/search-image?query=glass%20railing%20installation%20on%20staircase%20in%20luxury%20Las%20Vegas%20home%2C%20professional%20craftsman%20at%20work%2C%20clear%20tempered%20glass%20panels&width=500&height=400&seq=about002&orientation=landscape" alt="Glass railing installation" className="w-full h-full object-cover object-top" />
              </div>
              <div className="rounded-2xl overflow-hidden h-48">
                <img src="https://readdy.ai/api/search-image?query=custom%20mirror%20installation%20in%20modern%20bathroom%20Las%20Vegas%2C%20large%20frameless%20mirror%2C%20professional%20installation&width=500&height=300&seq=about003&orientation=landscape" alt="Custom mirror Las Vegas" className="w-full h-full object-cover object-top" />
              </div>
              <div className="rounded-2xl overflow-hidden h-48 mt-4">
                <img src="https://readdy.ai/api/search-image?query=glass%20company%20Las%20Vegas%20workshop%2C%20glass%20cutting%20and%20fabrication%2C%20professional%20glass%20shop%20interior&width=500&height=300&seq=about004&orientation=landscape" alt="Quinolux glass workshop" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: '15+', label: 'Years in Las Vegas' },
              { num: '2,000+', label: 'Projects Completed' },
              { num: '500+', label: '5-Star Reviews' },
              { num: '100%', label: 'Satisfaction Rate' },
            ].map(s => (
              <div key={s.label}>
                <p className="text-4xl md:text-5xl font-bold text-black">{s.num}</p>
                <p className="text-gray-500 text-sm mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">What We Stand For</span>
            <h2 className="text-4xl font-light text-black" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Our Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(v => (
              <div key={v.title} className="bg-[#F8F8F6] rounded-2xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-black text-white mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h3 className="text-black font-semibold text-base mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">The People Behind the Glass</span>
            <h2 className="text-4xl font-light text-black" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Meet Our Team</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map(m => (
              <div key={m.name} className="bg-white rounded-2xl overflow-hidden group">
                <div className="h-56 overflow-hidden">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <h3 className="text-black font-semibold text-base">{m.name}</h3>
                  <p className="text-gray-500 text-xs mt-0.5">{m.role}</p>
                  <p className="text-gray-400 text-xs mt-1 flex items-center gap-1"><i className="ri-time-line"></i>{m.years}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-4xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready to Work With Us?</h2>
          <p className="text-gray-400 text-sm mb-8">Get a free estimate for your glass project anywhere in the Las Vegas Valley.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/free-quote" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">Get Free Quote</Link>
            <a href="tel:+17026042093" className="inline-flex items-center gap-2 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-colors whitespace-nowrap cursor-pointer"><i className="ri-phone-fill"></i>(702) 604-2093</a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default AboutPage;
